import { Crown, Share2, Download } from 'lucide-react';
import { TeamLogo } from './TeamLogo';
import MedalBadge from './MedalBadge';
import { shareChampionsCard, downloadChampionsCard } from '../utils/championsCard';

// Compute 1st / 2nd / 3rd for a single division.
// SE/DE → from the final + (optional) 3rd-place match. RR → from standings once all matches are done.
export function computePodium(format, matches, standingsRows) {
  if (format === 'round_robin') {
    const rows = standingsRows || [];
    const allDone = matches.length > 0 && matches.every(m => ['completed', 'bye', 'cancelled'].includes(m.status));
    if (!allDone || rows.length === 0) return null;
    const pick = (r) => r ? { team_id: r.team_id, team_name: r.team_name } : null;
    return { first: pick(rows[0]), second: pick(rows[1]), third: pick(rows[2]) };
  }
  const resetM = matches.find(m => m.bracket_type === 'final_reset');
  const gf1 = matches.find(m => m.bracket_type === 'final');
  let fin = null;
  if (resetM && (resetM.status === 'pending' || resetM.status === 'live')) {
    fin = null;
  } else if (resetM && resetM.status === 'completed') {
    fin = resetM;
  } else if (gf1 && gf1.status === 'completed') {
    fin = gf1;
  } else if (format === 'single_elimination') {
    const nonThird = matches.filter(m => m.bracket_type !== 'third_place' && m.bracket_type !== 'losers' && m.stage !== 'pool');
    if (nonThird.length) {
      const maxRound = Math.max(...nonThird.map(m => m.round || 0));
      fin = nonThird.filter(m => (m.round || 0) === maxRound && m.status === 'completed' && m.winner_side)[0] || null;
    }
  }
  if (!fin || !fin.winner_side) return null;
  const w = fin.winner_side;
  const first = { team_id: w === 'a' ? fin.team_a_id : fin.team_b_id, team_name: w === 'a' ? fin.team_a_name : fin.team_b_name };
  const second = { team_id: w === 'a' ? fin.team_b_id : fin.team_a_id, team_name: w === 'a' ? fin.team_b_name : fin.team_a_name };
  let third = null;
  const bronze = matches.find(m => m.bracket_type === 'third_place' && m.status === 'completed' && m.winner_side);
  if (bronze) {
    const bw = bronze.winner_side;
    third = { team_id: bw === 'a' ? bronze.team_a_id : bronze.team_b_id, team_name: bw === 'a' ? bronze.team_a_name : bronze.team_b_name };
  }
  return { first, second, third };
}

const TIERS = {
  1: { color: '#FFD54A', glow: '#FFD54A', label: 'Champion', h: 118, size: 76 },
  2: { color: '#CBD5E1', glow: '#94A3B8', label: 'Runner-up', h: 86, size: 60 },
  3: { color: '#E0A86B', glow: '#CD7F32', label: '3rd Place', h: 66, size: 56 },
};

// Format player names based on team size
function PlayerNames({ players = [], maxSize = 4 }) {
  if (!players || players.length === 0) return null;
  const names = players.map(p => p.name).filter(Boolean);
  if (names.length === 0) return null;
  const LIMIT = maxSize <= 2 ? names.length : (maxSize <= 4 ? names.length : 3);
  const shown = names.slice(0, LIMIT);
  const extra = names.length - shown.length;
  return (
    <div style={{ marginTop: 3, textAlign: 'center' }}>
      {shown.map((n, i) => (
        <p key={i} style={{ fontSize: 10, color: '#A3A3A3', lineHeight: '14px', fontWeight: 500 }}>{n}</p>
      ))}
      {extra > 0 && <p style={{ fontSize: 10, color: '#525252' }}>+{extra} more</p>}
    </div>
  );
}

function Step({ place, team, logoMap, onLogo, players }) {
  if (!team) return null;
  const tier = TIERS[place];
  const logo = logoMap?.[team.team_id];
  return (
    <div className="flex flex-col items-center" style={{ width: place === 1 ? 130 : 110 }}>
      <div style={{ position: 'relative', marginBottom: 8 }}>
        <div style={{
          width: tier.size, height: tier.size, borderRadius: '50%', overflow: 'hidden',
          border: `3px solid ${tier.color}`, boxShadow: `0 0 18px ${tier.glow}55`,
          background: '#0A0A0A', display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <TeamLogo logo={logo} name={team.team_name} size={tier.size - 6} onClick={onLogo} ring="transparent" />
        </div>
        {place === 1 && <Crown style={{ position: 'absolute', top: -16, left: '50%', transform: 'translateX(-50%)', width: 24, height: 24, color: '#FFD54A' }} />}
        <span style={{ position: 'absolute', bottom: -6, right: -6 }}><MedalBadge rank={place} size={22} /></span>
      </div>
      <p className="text-center font-bold truncate" style={{ color: '#fff', fontSize: place === 1 ? 14 : 13, maxWidth: place === 1 ? 128 : 108 }}>{team.team_name}</p>
      <p className="text-center" style={{ color: tier.color, fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 1 }}>{tier.label}</p>
      <PlayerNames players={players} maxSize={players?.length || 0} />
      <div style={{
        marginTop: 8, width: place === 1 ? 110 : 92, height: tier.h, borderRadius: '8px 8px 0 0',
        background: `linear-gradient(180deg, ${tier.color}33, ${tier.color}10)`,
        border: `1px solid ${tier.color}55`, borderBottom: 'none',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', paddingTop: 8,
      }}>
        <span style={{ fontFamily: 'Oswald, sans-serif', fontSize: place === 1 ? 34 : 26, fontWeight: 900, color: tier.color }}>{place}</span>
      </div>
    </div>
  );
}

// Renders one division's podium (2nd · 1st · 3rd layout).
// teams: full teams array (with players) — used to hydrate player names on podium.
// cardMeta (optional): { eventName, facilityName, date } — shows Share/Download champions card buttons.
export default function DivisionPodium({ divName, podium, logoMap, onLogo, teams = [], cardMeta }) {
  if (!podium || !podium.first) return null;
  const getPlayers = (teamId) => (teams.find(t => t.id === teamId || t.team_id === teamId) || {}).players || [];
  const cardPayload = () => {
    const hydrate = (e) => e ? { ...e, players: (e.players?.length ? e.players : getPlayers(e.team_id).map(p => p.name).filter(Boolean)) } : null;
    return {
      ...cardMeta, division: divName,
      podium: { first: hydrate(podium.first), second: hydrate(podium.second), third: hydrate(podium.third) },
    };
  };
  return (
    <div data-testid={`podium-${divName || 'all'}`} className="rounded-2xl p-5" style={{ background: 'radial-gradient(circle at 50% 0%, #1A1505, #0C0C0C 70%)', border: '1px solid #2A2410' }}>
      <div className="flex items-center justify-center gap-2 mb-4">
        <Crown className="h-4 w-4" style={{ color: '#FFD54A' }} />
        <p className="text-sm font-bold uppercase tracking-widest" style={{ color: '#FFD54A', fontFamily: 'Oswald, sans-serif' }}>
          {divName ? `${divName} — Champions` : 'Champions'}
        </p>
      </div>
      <div className="flex items-end justify-center gap-2 sm:gap-4">
        <Step place={2} team={podium.second} logoMap={logoMap} onLogo={onLogo} players={getPlayers(podium.second?.team_id)} />
        <Step place={1} team={podium.first} logoMap={logoMap} onLogo={onLogo} players={getPlayers(podium.first?.team_id)} />
        <Step place={3} team={podium.third} logoMap={logoMap} onLogo={onLogo} players={getPlayers(podium.third?.team_id)} />
      </div>
      {cardMeta && (
        <div className="flex items-center justify-center gap-2 mt-4">
          <button data-testid={`share-champions-card-${divName || 'all'}`} onClick={() => shareChampionsCard(cardPayload())}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase"
            style={{ background: '#FFD54A1A', color: '#FFD54A', border: '1px solid #FFD54A40', fontFamily: 'Oswald, sans-serif' }}>
            <Share2 className="h-3.5 w-3.5" /> Share Card
          </button>
          <button data-testid={`download-champions-card-${divName || 'all'}`} onClick={() => downloadChampionsCard(cardPayload())}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold uppercase"
            style={{ background: '#FFFFFF0A', color: '#A3A3A3', border: '1px solid #2A2A2A', fontFamily: 'Oswald, sans-serif' }}>
            <Download className="h-3.5 w-3.5" /> Download
          </button>
        </div>
      )}
    </div>
  );
}
